// Messages: ICU MessageFormat in flat FormatJS JSON, German first (ARCHITECTURE 19.6).
//
// Two namespaces live outside the app, in i18n/messages: "common" (the product name and what every surface
// shares) and "app" (this app). The build compiles them to FormatJS ASTs (vite.config.ts, "?ast"), so the
// browser formats with intl-messageformat and carries no ICU parser. Further locales load their own files later
// (task L4-27). The ids are typed from the plain German files: a mistyped id does not compile.
//
// {brand} is available in every message and always holds the value of "brand.name": the product name is
// provisional (docs/strategy/NAME.md) and is never typed into copy.

import type { MessageFormatElement } from "@formatjs/icu-messageformat-parser";
import appAst from "@i18n/messages/app/de.json?ast";
import commonAst from "@i18n/messages/common/de.json?ast";
import type appMessages from "@i18n/messages/app/de.json";
import type commonMessages from "@i18n/messages/common/de.json";
import { IntlMessageFormat } from "intl-messageformat";

export type MessageId = keyof typeof commonMessages | keyof typeof appMessages;
export type MessageValues = Record<string, string | number | boolean | Date | null | undefined>;

const LOCALE = "de-DE";
const catalogue: Record<string, MessageFormatElement[]> = { ...commonAst, ...appAst };
const formats = new Map<string, IntlMessageFormat>();

function format(id: string): IntlMessageFormat {
  let message = formats.get(id);
  if (message === undefined) {
    const ast = catalogue[id];
    // Unreachable for a typed id; it guards a catalogue that changed under a running page.
    if (ast === undefined) throw new Error(`Unknown message "${id}".`);
    message = new IntlMessageFormat(ast, LOCALE);
    formats.set(id, message);
  }
  return message;
}

function text(id: string, values?: MessageValues): string {
  const result = format(id).format(values);
  if (typeof result !== "string") throw new Error(`Message "${id}" has rich text: format it with a tag handler.`);
  return result;
}

const brand = text("brand.name");

export function t(id: MessageId, values?: MessageValues): string {
  return text(id, { brand, ...values });
}

/** The translate function for components. It is the place that learns the active locale when more than German exists. */
export function useT(): typeof t {
  return t;
}
